import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, useToast, errText, useCaptchaConfirm, TenantSelect } from '../ui';

// Reachable two ways:
//  - /admin/tenants/:clientCode/organization   (linked from Tenant Detail)
//  - /admin/tenants/tools/organization          (linked from the sidebar directly)
export default function OrganizationProfile() {
  const { clientCode: routeClientCode } = useParams();
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  const [clientCode, setClientCode] = useState(routeClientCode || '');
  const [tenantDbName, setTenantDbName] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async (cc) => {
    setForm(null);
    try {
      const tenant = await tenantApi.getTenant(cc);
      const dbRes = await tenantApi.getDatabase(tenant.data.clientName);
      const db = dbRes.tenantDbName;
      setTenantDbName(db);
      const res = await tenantApi.getOrganization(db);
      setForm({
        organisationName: res.organisationName || '',
        email: res.email || '',
        mobileNo: res.mobileNo || '',
        landlineNo: res.landlineNo || '',
        address: res.address || '',
      });
    } catch (err) {
      setForm({ organisationName: '', email: '', mobileNo: '', landlineNo: '', address: '' });
      toast.error(errText(err, 'Could not load organization profile — starting blank.'));
    }
  };

  useEffect(() => {
    if (clientCode) load(clientCode);
  }, [clientCode]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      await tenantApi.setOrganization({ ...form, tenantDbName });
      toast.success('Organization profile updated.');
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    const ok = await confirm('Clear the organization profile for this tenant?');
    if (!ok) return;
    setClearing(true);
    try {
      await tenantApi.clearOrganization(tenantDbName);
      toast.success('Organization profile cleared.');
      load(clientCode);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      {confirmNode}
      <h1 className="page-title">organization_profile</h1>
      <p className="page-sub">
        {routeClientCode && (
          <>
            <Link to={`/admin/tenants/${routeClientCode}`} style={{ color: 'var(--text-dim)' }}>← {routeClientCode}</Link>
            {'  /  '}
          </>
        )}
        {tenantDbName && <>db: <b style={{ color: 'var(--green)' }}>{tenantDbName}</b></>}
      </p>

      {!routeClientCode && (
        <Panel title="// LOOKUP_TENANT">
          <div className="toolbar">
            <div className="toolbar__group">
              <TenantSelect value={clientCode} onSelect={(cc) => setClientCode(cc)} />
            </div>
          </div>
        </Panel>
      )}

      {clientCode && (
        <Panel title="// ORGANIZATION">
          {!form ? <Loader label="fetching organization profile" /> : (
            <>
              <div className="form-grid">
                <div className="field field--full">
                  <label>organisationName</label>
                  <input className="term-input" value={form.organisationName} onChange={(e) => setForm({ ...form, organisationName: e.target.value })} />
                </div>
                <div className="field">
                  <label>email</label>
                  <input className="term-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="field">
                  <label>mobileNo</label>
                  <input className="term-input" value={form.mobileNo} onChange={(e) => setForm({ ...form, mobileNo: e.target.value })} />
                </div>
                <div className="field">
                  <label>landlineNo</label>
                  <input className="term-input" value={form.landlineNo} onChange={(e) => setForm({ ...form, landlineNo: e.target.value })} />
                </div>
                <div className="field field--full">
                  <label>address</label>
                  <input className="term-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="term-btn term-btn--solid" disabled={saving} onClick={handleSave}>
                  {saving ? 'saving...' : 'save_changes'}
                </button>
                <button className="term-btn term-btn--danger" disabled={clearing} onClick={handleClear}>
                  {clearing ? 'clearing...' : 'clear_profile'}
                </button>
              </div>
            </>
          )}
        </Panel>
      )}
    </>
  );
}
